import { Component, Input, OnInit, OnDestroy } from "@angular/core";
import { Select } from "@ngxs/store";
import { Subscription, Observable } from "rxjs";
import { UserState } from "../../store/user/user.store";
import { User } from "../../shared/models/user.model";
import { AsyncPipe, CommonModule, NgIf } from "@angular/common";
import { LocalizationModule } from "../localization";
import { MatIconModule } from "@angular/material/icon";
import { SignInSignUpComponent } from "../signin-signup/signin-signup.component";
import { UserActionsComponent } from "../user-actions/user-actions.component";
import { MatButtonModule } from "@angular/material/button";
import { Dialog, DialogModule } from "@angular/cdk/dialog";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { OverlayModule } from "@angular/cdk/overlay";
import { IconStore } from "src/assets/icons/mobile-menu-panel/store";
import { IconAthletes } from "src/assets/icons/mobile-menu-panel/athletes";
import { IconEvents } from "src/assets/icons/mobile-menu-panel/events";
import { IconLeagues } from "src/assets/icons/mobile-menu-panel/leagues";
import { IconNews } from "src/assets/icons/mobile-menu-panel/news";
import { IconRankings } from "src/assets/icons/mobile-menu-panel/rankings";
import { IconStudios } from "src/assets/icons/mobile-menu-panel/studios";
import { IconTickets } from "src/assets/icons/mobile-menu-panel/tickets";
import { IconTV } from "src/assets/icons/mobile-menu-panel/tv";
import { SafePipe } from "src/app/shared/pipes/safe.pipe";
import { SafeTypes } from "src/app/shared/constants/safe-types.enum";
import { ConnectionPositionPair } from "@angular/cdk/overlay";
import { RouterModule } from "@angular/router";
import { environment } from "src/environments/environment";
import { AuthService } from "src/app/shared/services/auth.service";
import { ApplicationService } from "src/app/shared/constants/application.service";
import { StudiosService } from "src/app/shared/constants/studios.service";

@Component({
  selector: "studio-user-settings",
  standalone: true,
  templateUrl: "./user-settings.component.html",
  styleUrls: ["./user-settings.component.scss"],
  imports: [
    AsyncPipe,
    LocalizationModule,
    MatIconModule,
    NgIf,
    UserActionsComponent,
    MatButtonModule,
    MatDialogModule,
    DialogModule,
    SignInSignUpComponent,
    CommonModule,
    OverlayModule,
    SafePipe,
    RouterModule,
  ],
})
export class UserSettingsComponent implements OnInit, OnDestroy {
  @Input() isDiffUrl!: boolean;

  @Select(UserState.authUser)
  public authUser$!: Observable<User>;

  @Select(UserState.getPrimaryRoleName)
  public getRoleName$!: Observable<string>;
  @Input() hidePanelsOnOpenSearch!: boolean;
  isAAUSubdomain: boolean = false;
  isOWTMASubdomain: boolean = false;
  hasStudios: boolean | null = null;

  private authSubscription!: Subscription;
  private isSignInDialogOpening = false;

  constructor(private dialog: MatDialog, private auth: AuthService, private applicationService: ApplicationService, private studiosService: StudiosService) { }

  ngOnInit() {
    this.authSubscription = this.auth.openSignInDialog$.subscribe((data) => {
      if (data) {
        if (!this.isSignInDialogOpening) {
          const isDialogOpen = this.dialog.openDialogs.some(d => d.componentInstance instanceof SignInSignUpComponent);
          if (!isDialogOpen) {
            this.isSignInDialogOpening = true;
            const dialogRef = this.dialog.open(SignInSignUpComponent, {
              autoFocus: "dialog",
              panelClass: 'custom-dialog-container',
              restoreFocus: false,
              data: data.open ? undefined : data
            });

            dialogRef.afterOpened().subscribe(() => {
              this.isSignInDialogOpening = false;
            });
            dialogRef.afterClosed().subscribe(() => {
              this.isSignInDialogOpening = false;
            });
          }
        }
        // Reset to avoid opening again on next subscription
        this.auth.openSignInDialog$.next(null);
      }
    });

    this.applicationService.aauSubdomain$.subscribe(isAAUSubdoamin => {
      if(isAAUSubdoamin === null){
        return;
      }
      this.isAAUSubdomain = isAAUSubdoamin;
    })

    this.applicationService.owtmaSubdomain$.subscribe(isOWTMASubdomain => {
      this.isOWTMASubdomain = !!isOWTMASubdomain;
    })

    this.studiosService.hasStudios$.subscribe(hasStudios => {
      this.hasStudios = hasStudios;
    })
    this.studiosService.loadHasStudios();
  }

  ngOnDestroy() {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }
  public baseUrl = environment.BASEURL.slice(
    environment.BASEURL.indexOf("/") + 2,
    -1
  );
  public isOpen = false;
  public SafeTypes = SafeTypes;
  public IconStore = IconStore;
  public IconAthletes = IconAthletes;
  public IconEvents = IconEvents;
  public IconLeagues = IconLeagues;
  public IconNews = IconNews;
  public IconRankings = IconRankings;
  public IconStudios = IconStudios;
  public IconTickets = IconTickets;
  public IconTV = IconTV;

  public signIn(): void {
    this.auth.openSignIn();
  }

  public filterPosition = [
    new ConnectionPositionPair(
      { originX: "start", originY: "bottom" },
      { overlayX: "end", overlayY: "top" },
      102,
      28,
      "mobile-menu-panel"
    ),
  ];

  public getPanelDimensions(): { width: string; height: string } {
    // Calculate based on number of items
    // AAU subdomain: 2 items (1 row × 2 columns)
    // Regular: 9 items (3 rows × 3 columns)
    
    const itemSize = 64; // width and height of each item
    const gap = 8; // gap between items
    const padding = 8; // padding around the panel
    
    if (this.isAAUSubdomain) {
      // 2 items in a row: padding + item + gap + item + padding
      const width = padding + itemSize + gap + itemSize + padding; // 152px
      // 1 row: padding + item + padding
      const height = padding + itemSize + padding; // 80px
      return { width: `${width}px`, height: `${height}px` };
    } else if (this.isOWTMASubdomain) {
      // 3 items in a single row
      const width = padding + (itemSize * 3) + (gap * 2) + padding; // 228px
      const height = padding + itemSize + padding; // 80px
      return { width: `${width}px`, height: `${height}px` };
    } else {
      // 3 items per row, 3 rows
      const width = padding + (itemSize * 3) + (gap * 2) + padding; // 228px
      const height = padding + (itemSize * 3) + (gap * 2) + padding; // 228px
      return { width: `${width}px`, height: `${height}px` };
    }
  }
}
